import React, { FC, useEffect, useMemo, useState } from 'react'

import { Switch } from '@style-port/roov-ui-components-react'
import { DecorationTitle, ErrorTxt, Txt } from 'src/components/atoms'
import { SettingButtons, UserRoleTxt, WorksRoleDropdown } from 'src/components/molecules'
import { SelectCustomRoleDropdown } from 'src/components/organisms/customRoles/SelectCustomRoleDropdown'

import {
  useAuthority,
  useGetCustomRoles,
  useGetProfile,
  useGetUser,
  useGetUsers,
  useMasterData,
  useToast,
  useUser,
} from 'src/hooks'
import { isSameElementsIncluded } from 'src/libs/helper'
import { ProfileEntity, InternalUserDetailEntity, WorksRoleType } from 'src/types'

type Props = {
  user: InternalUserDetailEntity | ProfileEntity
  className?: string
}

export const InternalUserRole: FC<Props> = ({ user, className }) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [updatedWorksRole, setUpdatedWorksRole] = useState<WorksRoleType>(user.role)

  const [selectedCustomRoleIds, setSelectedCustomRoleIds] = useState<number[]>(
    user.customRoles.map(customRole => customRole.id)
  )

  const { masterData } = useMasterData()
  const { profile, refetch: profileRefetch } = useGetProfile()
  const { users, refetch: usersRefetch } = useGetUsers()
  const { refetch: userRefetch } = useGetUser({ userId: user.id })
  const { displayStandardTextToast, displayErrorTextToast } = useToast()
  const { updateRole, updateUserCustomRoles } = useUser()
  const { canUpdateRole } = useAuthority()
  const { customRoles, refetch: customRolesRefetch } = useGetCustomRoles()

  const selectedCustomRoles = useMemo(() => {
    return customRoles.filter(customRole => selectedCustomRoleIds.includes(customRole.id))
  }, [customRoles, selectedCustomRoleIds])

  const existsSuperAdminUser = useMemo(() => {
    if (!users) {
      return false
    }

    return (
      users.filter(u => u.id !== user.id).some(u => u.role.name === 'superAdmin') ||
      updatedWorksRole.name === 'superAdmin'
    )
  }, [updatedWorksRole, user.id, users])

  const showSuperAdminMessage = useMemo(
    () => isEditMode && !existsSuperAdminUser,
    [existsSuperAdminUser, isEditMode]
  )

  const enabledCheckButton = useMemo(() => {
    return (
      (user.role.id !== updatedWorksRole.id /* worksのロールが初期値から変わっている */ ||
        !isSameElementsIncluded(
          user.customRoles.map(customRole => customRole.id),
          selectedCustomRoleIds
        )) /* カスタムロールが初期値から変わっている */ &&
      !showSuperAdminMessage
    )
  }, [
    selectedCustomRoleIds,
    showSuperAdminMessage,
    updatedWorksRole.id,
    user.customRoles,
    user.role.id,
  ])

  const handleSelectOption = (value: number) => {
    if (!masterData) {
      return
    }
    const selectedRole = masterData.worksRoles.find(role => role.id === value)
    if (!selectedRole) {
      return
    }
    setUpdatedWorksRole(selectedRole)
  }

  const handleUpdateRole = async () => {
    setIsUpdating(true)

    let functions: Promise<void>[] = []

    // worksのロールが変わっている場合は更新
    if (user.role.id !== updatedWorksRole.id) {
      functions = [...functions, updateRole({ userId: user.id, roleId: updatedWorksRole.id })]
    }
    // カスタムロールが変わっている場合は更新
    if (
      !isSameElementsIncluded(
        user.customRoles.map(customRole => customRole.id),
        selectedCustomRoleIds
      )
    ) {
      functions = [
        ...functions,
        updateUserCustomRoles({ userId: user.id, customRoleIds: selectedCustomRoleIds }),
      ]
    }

    try {
      await Promise.all(functions)
      // マイページでも利用しているため profile もrefetchする
      await Promise.all([usersRefetch(), userRefetch(), customRolesRefetch(), profileRefetch()])
      displayStandardTextToast('更新しました')
    } catch {
      displayErrorTextToast('更新に失敗しました')
    } finally {
      setIsUpdating(false)
      setIsEditMode(false)
    }
  }

  const cancel = () => {
    setUpdatedWorksRole(user.role)
    setSelectedCustomRoleIds(user.customRoles.map(customRole => customRole.id))
    setIsEditMode(false)
  }

  useEffect(() => {
    // ユーザー情報が更新されたら編集モードを終了する
    setIsUpdating(false)
    setIsEditMode(false)
  }, [user])

  return (
    <div className={className}>
      <div className="tw-full-justify">
        <DecorationTitle>WORKS役割</DecorationTitle>
        {canUpdateRole({ targetUser: user }) && (
          <SettingButtons
            isEditMode={isEditMode}
            isLoading={isUpdating}
            updateIsEditMode={setIsEditMode}
            enabledCheckButton={enabledCheckButton}
            onClickCancel={cancel}
            finishEdit={handleUpdateRole}
          />
        )}
      </div>
      <div className="tw-mt-s2">
        {isEditMode ? (
          <div>
            {masterData && profile ? (
              <WorksRoleDropdown
                worksRoles={masterData.worksRoles}
                profile={profile}
                value={updatedWorksRole.id}
                selectOption={handleSelectOption}
                disabled={isUpdating || (profile && profile.id === user.id)}
                className="tw-w-2/3"
              />
            ) : null}
            <SelectCustomRoleDropdown
              alreadyAssignedCustomRoleIds={selectedCustomRoleIds}
              selectCustomRole={customRole => {
                setSelectedCustomRoleIds(previousStates => [...previousStates, customRole.id])
              }}
              removeCustomRole={customRole => {
                setSelectedCustomRoleIds(previousStates =>
                  previousStates.filter(id => id !== customRole.id)
                )
              }}
              className="tw-mt-s2"
            />
          </div>
        ) : (
          <div>
            <UserRoleTxt
              roleName={isUpdating ? updatedWorksRole.name : user.role.name}
              roleDisplayName={isUpdating ? updatedWorksRole.displayName : user.role.displayName}
              className="tw-w-fit"
            />
            {selectedCustomRoles.length > 0 && (
              <ul className="tw-flex tw-gap-r-2 tw-mt-s2 tw-flex-wrap">
                {selectedCustomRoles.map(customRole => (
                  <li key={customRole.id}>
                    <Switch switchType="primary" className="tw-w-full">
                      <Txt>{customRole.name}</Txt>
                    </Switch>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {showSuperAdminMessage && (
        <ErrorTxt
          text="全権管理者は必ず1人存在するようにして下さい"
          showIcon
          className="tw-mt-s2"
        />
      )}
    </div>
  )
}
